import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';

const PricingTransparency = () => {
  const principles = [
    'Fixed project pricing (not hourly)',
    '50% advance, 50% on delivery',
    'Change requests priced upfront',
    'Maintenance optional (₹5k-15k/month)',
    'No surprise bills',
  ];

  const comparisonData = [
    { feature: 'Timeline', traditional: '6-12 months', litheforce: '2-8 weeks', positive: true },
    { feature: 'Pricing', traditional: '₹50L-2Cr', litheforce: '₹50k-20L', positive: true },
    { feature: 'Communication', traditional: 'Via PMs', litheforce: 'Direct founders', positive: true },
    { feature: 'Tech Stack', traditional: 'Legacy', litheforce: 'AI-native', positive: true },
    { feature: 'Flexibility', traditional: 'Rigid', litheforce: 'Agile', positive: true },
    { feature: 'Hidden Costs', traditional: 'Common', litheforce: 'Never', positive: true },
  ];

  return (
    <section id="pricing" className="section-padding bg-[#f8fafc]" aria-label="Pricing" data-testid="pricing-section">
      <div className="section-container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="text-sm font-medium tracking-wide uppercase text-[#ff6d00] mb-3 block">
            Why Us
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#212121] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="pricing-headline"
          >
            No Hidden Costs. Ever.
          </h2>
          <p className="text-base md:text-lg text-[#5a6b8c] max-w-2xl mx-auto">
            What you see is what you pay
          </p>
        </motion.div>

        {/* Pricing Principles */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex flex-wrap justify-center gap-4 mb-12 md:mb-16"
        >
          {principles.map((principle, index) => (
            <div
              key={index}
              className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-100 shadow-sm"
            >
              <Check className="w-4 h-4 text-green-500" />
              <span className="text-sm text-[#5a6b8c]">{principle}</span>
            </div>
          ))}
        </motion.div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-3 bg-[#1a237e] text-white">
              <div className="p-4 md:p-6 font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Feature
              </div>
              <div className="p-4 md:p-6 font-semibold text-center border-l border-white/10" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Traditional Agency
              </div>
              <div className="p-4 md:p-6 font-semibold text-center border-l border-white/10 bg-[#00e5ff]/20" style={{ fontFamily: 'Outfit, sans-serif' }}>
                LitheForce
              </div>
            </div>

            {/* Table Body */}
            {comparisonData.map((row, index) => (
              <div
                key={index}
                className={`grid grid-cols-3 ${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}
              >
                <div className="p-4 md:p-6 font-medium text-[#212121]">{row.feature}</div>
                <div className="p-4 md:p-6 text-center border-l border-slate-100 text-[#5a6b8c] flex items-center justify-center gap-2">
                  <X className="w-4 h-4 text-red-400" />
                  <span className="text-sm md:text-base">{row.traditional}</span>
                </div>
                <div className="p-4 md:p-6 text-center border-l border-slate-100 bg-[#00e5ff]/5 flex items-center justify-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm md:text-base font-medium text-[#1a237e]">{row.litheforce}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingTransparency;
